import { Volume2, Play, Info, Link2, ImageIcon, ArrowDown, Plus, MousePointerClick, Type, ListChecks, Star, HelpCircle, Eye, Lightbulb, Sparkles, Search, Heart, MapPin, Hand } from "lucide-react";
import { fileUrl } from "@/lib/api";

export const TYPE_META = {
  hotspot: { label: "Hotspot", icon: MousePointerClick },
  audio: { label: "Audio", icon: Volume2 },
  video: { label: "Video", icon: Play },
  image: { label: "Image", icon: ImageIcon },
  popup: { label: "Popup", icon: Info },
  link: { label: "Link", icon: Link2 },
  text: { label: "Animation", icon: Type },
  quiz: { label: "Quiz", icon: ListChecks },
};

export const ICONS = { plus: Plus, info: Info, play: Play, sound: Volume2, star: Star, question: HelpCircle, eye: Eye, idea: Lightbulb, sparkle: Sparkles, search: Search, heart: Heart, pin: MapPin, tap: Hand, arrow: ArrowDown, link: Link2, image: ImageIcon };
export const DEFAULT_ACCENT = { hotspot: "#A62B8A", audio: "#3D3DF0", popup: "#111111", quiz: "#3D3DF0" };
export const accentOf = (l) => l.accent || DEFAULT_ACCENT[l.type] || "#3D3DF0";

const uid = () => Math.random().toString(36).slice(2, 10);

export const makeLayer = (type, extra = {}) => {
  const base = { id: uid(), type, name: TYPE_META[type].label, x: 40, y: 40, w: 10, h: 8, hidden: false, locked: false, anim: "none", animDelay: 0, alt: "" };
  const per = {
    hotspot: { style: "pulse", idle: "glow", hover: "lift", action: "popup", title: "Did you know?", content: "Add your explanation here.", w: 7, h: 5 },
    audio: { idle: "glow", hover: "lift", autoplay: false, loop: false, w: 7, h: 5, alt: "Play narration" },
    video: { mode: "inline", autoplay: false, muted: true, loop: false, controls: true, w: 40, h: 25, x: 30, y: 30 },
    image: { lightbox: true, w: 25, h: 20, x: 37, y: 35 },
    popup: { idle: "glow", hover: "lift", style: "icon", title: "More info", content: "Write popup content here.", w: 7, h: 5 },
    link: { style: "outline", url: "https://", page: null, w: 20, h: 6 },
    quiz: { idle: "breathe", hover: "lift", title: "Knowledge check", label: "Start quiz", pass: 70, retry: true, w: 22, h: 5, x: 39, y: 45,
      questions: [{ type: "mcq", prompt: "Your question?", options: ["Option A", "Option B", "Option C"], answer: 0, feedback: "" }, { type: "tf", prompt: "This statement is true.", options: ["True", "False"], answer: 0, feedback: "" }] },
    text: { content: "Animated text", color: "#FFFFFF", bg: "#3D3DF0", fontSize: 18, anim: "bounce", w: 30, h: 8, x: 35, y: 10 },
  };
  return { ...base, ...per[type], ...extra };
};

export const isEmbedUrl = (u) => /youtube\.com|youtu\.be|vimeo\.com/.test(u || "");
export const embedUrl = (u) => {
  const yt = (u || "").match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0`;
  const vm = (u || "").match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}?autoplay=1`;
  return u;
};

const HotspotVisual = ({ layer }) => {
  const s = layer.style;
  const c = accentOf(layer);
  if (s === "invisible") return <div className="h-full w-full" />;
  if (s === "glow") return <div className="h-full w-full rounded-md hs-glow" style={{ "--hs": c }} />;
  if (s === "circle") return <div className="h-full w-full rounded-full border-[3px]" style={{ borderColor: c, background: `${c}26` }} />;
  if (s === "arrow") return <div className="grid h-full w-full place-items-center"><ArrowDown className="hs-bob h-3/4 w-3/4 drop-shadow-lg" style={{ color: c }} strokeWidth={3} /></div>;
  if (s === "image" && layer.icon) return <img src={fileUrl(layer.icon)} alt="" className="h-full w-full object-contain" />;
  const Icon = ICONS[layer.iconName] || { audio: Volume2, video: Play, image: ImageIcon, url: Link2, page: Link2 }[layer.action] || Plus;
  return (
    <div className="relative grid h-full w-full place-items-center">
      {s === "pulse" && <span className="absolute inset-0 m-auto aspect-square max-h-full max-w-full rounded-full hs-pulse-ring" style={{ background: c }} />}
      <span className="relative grid aspect-square h-full max-h-full max-w-full place-items-center rounded-full shadow-lg" style={{ background: c }}>
        <Icon className="lv-icon h-1/2 w-1/2 text-white" />
      </span>
    </div>
  );
};

const RoundIcon = ({ icon: Icon, color = "#3D3DF0" }) => (
  <div className="grid h-full w-full place-items-center">
    <span className="grid aspect-square h-full max-h-full max-w-full place-items-center rounded-full shadow-lg" style={{ background: color }}>
      <Icon className="lv-icon h-1/2 w-1/2 text-white" />
    </span>
  </div>
);

// Static visual used in editor and as the player's pre-interaction state.
export const LayerVisual = ({ layer }) => {
  switch (layer.type) {
    case "hotspot": return <HotspotVisual layer={layer} />;
    case "audio": return <RoundIcon icon={ICONS[layer.iconName] || Volume2} color={accentOf(layer)} />;
    case "popup":
      if (layer.style === "invisible") return <div className="h-full w-full" />;
      if (layer.style === "button") return <Pill icon={ICONS[layer.iconName] || Info} label={layer.label || "Learn more"} color={layer.accent || "#0F172A"} />;
      return <RoundIcon icon={ICONS[layer.iconName] || Info} color={accentOf(layer)} />;
    case "quiz": return <Pill icon={ListChecks} label={layer.label || "Start quiz"} className={layer.accent ? "" : "sg-gradient"} color={layer.accent} />;
    case "link": return <div className={`h-full w-full rounded ${layer.style === "outline" ? "border-2 border-dashed border-[#3D3DF0] bg-[#3D3DF0]/10" : ""}`} />;
    case "image": return layer.src ? <img src={fileUrl(layer.src)} alt={layer.alt} loading="lazy" className="h-full w-full object-cover" draggable={false} /> : <Placeholder icon={ImageIcon} label="Image" />;
    case "video": return (
      <div className="relative h-full w-full overflow-hidden rounded bg-black">
        {layer.poster && <img src={fileUrl(layer.poster)} alt="" className="h-full w-full object-cover" draggable={false} />}
        <div className="absolute inset-0 grid place-items-center"><span className="grid h-12 w-12 place-items-center rounded-full bg-white/90 text-black shadow-xl"><Play className="ml-0.5 h-5 w-5" fill="currentColor" /></span></div>
      </div>
    );
    case "text": return (
      <div className="grid h-full w-full place-items-center rounded px-2 text-center font-display font-extrabold leading-tight" style={{ color: layer.color, background: layer.bg, fontSize: `${layer.fontSize}px` }}>{layer.content}</div>
    );
    default: return null;
  }
};

const Pill = ({ icon: Icon, label, className = "", color }) => (
  <div className={`flex h-full w-full items-center justify-center gap-1.5 overflow-hidden whitespace-nowrap rounded-full px-3 text-[clamp(9px,1.4cqh,14px)] font-bold text-white shadow-lg ${className}`} style={{ containerType: "size", ...(color ? { background: color } : {}) }}>
    <Icon className="lv-icon h-[55%] max-h-4 w-auto shrink-0" /><span className="truncate text-xs">{label}</span>
  </div>
);

const Placeholder = ({ icon: Icon, label }) => (
  <div className="grid h-full w-full place-items-center rounded border border-dashed border-[#8A8A8E] bg-black/40 text-xs text-white"><span className="flex items-center gap-1"><Icon className="h-4 w-4" />{label}</span></div>
);
